import React, { useState, useEffect } from "react";
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Divider,
  Grid,
  Button,
  TextField,
  Chip,
  useTheme,
  Avatar,
  InputAdornment,
  keyframes,
  styled,
  Badge
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import EmailIcon from "@mui/icons-material/Email";
import PrintIcon from "@mui/icons-material/Print";
import ReceiptIcon from "@mui/icons-material/Receipt";
import LocalAtmIcon from "@mui/icons-material/LocalAtm";
import DiscountIcon from "@mui/icons-material/Discount";
import LoyaltyIcon from "@mui/icons-material/Loyalty";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ComputerIcon from "@mui/icons-material/Computer";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { fetchOrders, Order, sendReceipt } from "../api/orderApi";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const ModernAccordion = styled(Accordion)(({ theme }) => ({
  borderRadius: '12px!important',
  overflow: 'hidden',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[6]
  },
  '&::before': {
    display: 'none'
  }
}));

const OrdersPage: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
const [selectedFilter, setSelectedFilter] = useState<
  'all' | 'paid' | 'pending' | 'online' | 'delivery' | 'mail'
>('all');
  const theme = useTheme();

  const formatAttributes = (attributes?: any) => {
    if (!attributes) return "";
    if (Array.isArray(attributes)) {
      return attributes.map(attr => `${attr.name}: ${attr.values.join(', ')}`).join(' • ');
    }
    if (typeof attributes === 'object') {
      return Object.entries(attributes).map(([key, value]) => `${key}: ${value}`).join(" • ");
    }
    return "";
  };

  useEffect(() => {
    const loadOrders = async () => {
      try {
        const fetchedOrders: Order[] = await fetchOrders();
        setOrders(fetchedOrders);
      } catch (error) {
        console.error("Error fetching orders:", error);
      }
    };
    loadOrders();
    const interval = setInterval(loadOrders, 10000);
    return () => clearInterval(interval);
  }, []);

const generateOrderPDF = (order: Order) => {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });

  // Calculate order values
  const subtotal = order.subtotal || 0;
  const discountValue = order.discount_value || 0;
  const discountAmount = order.discount_type === "percent" ? 
    (subtotal * discountValue) / 100 : discountValue;
  const loyaltyPointsUsed = order.loyalty_points_used || 0;
  const tax = order.tax || 0;
  const shippingCost = order.shipping_cost || 0;
  const totalPrice = Math.max(0, subtotal - discountAmount + tax + shippingCost - loyaltyPointsUsed);

  // Add logo
  const logoUrl = '/logo-tb.png';
  doc.addImage(logoUrl, 'PNG', 15, 10, 40, 15);

  // Header section
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(40);
  doc.text(`ORDER RECEIPT`, 105, 20, { align: 'center' });

  doc.setFontSize(12);
  doc.setTextColor(100);
  doc.text(`#${order.order_number}`, 105, 27, { align: 'center' });

  // Order details section
  doc.setFontSize(10);
  doc.setTextColor(80);
  doc.text(`Date: ${new Date(order.created_at).toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })}`, 15, 40);

  doc.text(`Status:`, 15, 46);
  doc.setTextColor(order.payment_status === "paid" ? '#4CAF50' : '#FF9800');
  doc.text(`${order.payment_status === "paid" ? "PAID" : "PENDING"}`, 30, 46);
  
  doc.setTextColor(80);
  doc.text(`Method: ${order.payment_method?.toUpperCase() || "N/A"}`, 15, 52);

  // Shipping Information Section
  doc.setFontSize(11);
  doc.setTextColor(40);
  doc.setFont("helvetica", "bold");
  doc.text("SHIPPING INFORMATION", 15, 62);
  
  doc.setFont("helvetica", "normal");
  doc.setTextColor(80);
  
  if (order.shipping_address) {
    doc.text(`Name: ${order.shipping_address.full_name || order.customer_name}`, 15, 68);
    doc.text(`Address: ${order.shipping_address.address || ''}`, 15, 74);
    
    const locationParts = [
      order.shipping_address.city,
      order.shipping_address.state,
      order.shipping_address.zip_code?.replace(/\s/g, '')
    ].filter(Boolean);
    
    doc.text(`Location: ${locationParts.join(', ').toLowerCase()}`, 15, 80);
    
    const phone = order.shipping_address.phone || order.customer_phone;
    const formattedPhone = phone && phone.length === 10 ? 
      `${phone.substring(0, 3)}-${phone.substring(3, 6)}-${phone.substring(6)}` : 
      phone || 'N/A';
    
    doc.text(`Phone: ${formattedPhone}`, 15, 86);
    doc.text(`Email: ${order.customer_email || 'N/A'}`, 15, 92);
  }

  // Items table
  autoTable(doc, {
    startY: order.shipping_address ? 97 : 70,
    head: [
      [
        { 
          content: 'PRODUCT',
          styles: { 
            fillColor: theme.palette.primary.main,
            textColor: 255,
            fontStyle: 'bold'
          }
        },
        { 
          content: 'DETAILS',
          styles: { 
            fillColor: theme.palette.primary.main,
            textColor: 255,
            fontStyle: 'bold'
          }
        },
        { 
          content: 'PRICE',
          styles: { 
            fillColor: theme.palette.primary.main,
            textColor: 255,
            fontStyle: 'bold',
            halign: 'right'
          }
        },
        { 
          content: 'QTY',
          styles: { 
            fillColor: theme.palette.primary.main,
            textColor: 255,
            fontStyle: 'bold',
            halign: 'center'
          }
        },
        { 
          content: 'TOTAL',
          styles: { 
            fillColor: theme.palette.primary.main,
            textColor: 255,
            fontStyle: 'bold',
            halign: 'right'
          }
        }
      ]
    ],
    body: order.items.map((item) => [
      { 
        content: item.product_name,
        styles: { fontStyle: 'bold' }
      },
      item.variant_name || formatAttributes(item.attributes),
      { 
        content: `$${item.price.toFixed(2)}`,
        styles: { halign: 'right' }
      },
      { 
        content: item.quantity.toString(),
        styles: { halign: 'center' }
      },
      { 
        content: `$${(item.quantity * item.price).toFixed(2)}`,
        styles: { halign: 'right' }
      },
    ]),
    styles: { 
      fontSize: 9,
      cellPadding: 3,
      lineColor: [221, 221, 221],
      lineWidth: 0.3
    },
    headStyles: {
      textColor: 255,
      fontStyle: 'bold'
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245]
    },
    columnStyles: {
      0: { cellWidth: 'auto' },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 25 },
      3: { cellWidth: 15 },
      4: { cellWidth: 25 }
    }
  });

  // Summary section
  const finalY = (doc as any).lastAutoTable.finalY + 15;
  doc.setDrawColor(200);
  doc.setLineWidth(0.5);
  doc.line(15, finalY - 5, 195, finalY - 5);

  doc.setFontSize(10);
  doc.setTextColor(80);
  
  const summaryRows = [
    { label: 'Subtotal:', value: `$${subtotal.toFixed(2)}` },
    { label: 'Discount:', value: `-$${discountAmount.toFixed(2)}` },
    { label: 'Loyalty Points:', value: `-$${loyaltyPointsUsed.toFixed(2)}` },
    { label: 'Tax:', value: `$${tax.toFixed(2)}` },
    { label: 'Shipping:', value: `$${shippingCost.toFixed(2)}` }
  ];

  summaryRows.forEach((row, index) => {
    doc.text(row.label, 140, finalY + (index * 6));
    doc.text(row.value, 190, finalY + (index * 6), { align: 'right' });
  });

  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(40);
  doc.text('TOTAL:', 140, finalY + 30);
  doc.text(`$${totalPrice.toFixed(2)}`, 190, finalY + 30, { align: 'right' });

  // Footer
  doc.setFontSize(8);
  doc.setTextColor(120);
  doc.text('Thank you for your business!', 105, 280, { align: 'center' });
  doc.text('Questions? Contact fchasergc@gmail.com', 105, 285, { align: 'center' });

  doc.save(`Order_${order.order_number}_Receipt.pdf`);
};

  const handleFilterClick = (
  filter: 'all' | 'paid' | 'pending' | 'online' | 'delivery' | 'mail'
) => {
  setSelectedFilter(filter);
};


  const filteredOrders = orders.filter((order) => {
  const matchesSearch =
    order.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.order_number.includes(searchTerm);

  const matchesFilter =
    selectedFilter === "all" ||
    (selectedFilter === "paid" && order.payment_status === "paid") ||
    (selectedFilter === "pending" && order.payment_status === "pending") ||
    (selectedFilter === "online" && order.is_online_order) ||
    (selectedFilter === "delivery" && order.is_delivery) ||
    (selectedFilter === "mail" && !order.is_delivery && order.is_online_order);

  return matchesSearch && matchesFilter;
});


  return (
    <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        mb: 4, 
        gap: 2, 
        flexWrap: 'wrap',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        backgroundColor: 'background.paper',
        py: 2
      }}>
        <Typography variant="h4" sx={{ 
          fontWeight: 700,
          background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          Order History
        </Typography>
        <TextField
          variant="outlined"
          placeholder="Search receipts or customers..."
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            sx: {
              borderRadius: 30,
              backgroundColor: theme.palette.mode === 'light' ? '#f5f5f5' : '#2a2a2a',
              transition: 'all 0.3s',
              '&:hover': {
                backgroundColor: theme.palette.mode === 'light' ? '#eee' : '#333'
              }
            }
          }}
          sx={{ width: 340 }}
        />
      </Box>

      <Box sx={{ 
        mb: 4, 
        display: 'flex', 
        gap: 1.5,
        '& .MuiChip-root': {
          borderRadius: 20,
          px: 1.5,
          py: 1.2,
          transition: 'all 0.2s',
          cursor: 'pointer'
        }
      }}>
        <Chip 
          label="All sales" 
          variant={selectedFilter === 'all' ? 'filled' : 'outlined'}
          color="primary"
          onClick={() => handleFilterClick('all')}
        />
        <Chip 
          label="Completed" 
          variant={selectedFilter === 'paid' ? 'filled' : 'outlined'}
          color="success"
          onClick={() => handleFilterClick('paid')}
        />
        <Chip 
          label="Pending" 
          variant={selectedFilter === 'pending' ? 'filled' : 'outlined'}
          color="warning"
          onClick={() => handleFilterClick('pending')}
        />
        <Chip 
          label="Online" 
          variant={selectedFilter === 'online' ? 'filled' : 'outlined'}
          color="secondary"
          onClick={() => handleFilterClick('online')}
          icon={<ComputerIcon fontSize="small" />}
        />
        <Chip 
  label="Delivery Orders"
  variant={selectedFilter === 'delivery' ? 'filled' : 'outlined'}
  color="info"
  onClick={() => handleFilterClick('delivery')}
  icon={<LocalShippingIcon fontSize="small" />}
/>

<Chip 
  label="Mail Orders"
  variant={selectedFilter === 'mail' ? 'filled' : 'outlined'}
  color="default"
  onClick={() => handleFilterClick('mail')}
  icon={<ReceiptIcon fontSize="small" />}
/>

      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: 3
          }}>
            {filteredOrders.map((order) => {
              const subtotal = order.subtotal || 0;
              const discountValue = order.discount_value || 0;
              const discountAmount = order.discount_type === "percent" ? 
                (subtotal * discountValue) / 100 : discountValue;
              const shippingCost = order.shipping_cost || 0;
              const totalPrice = subtotal - discountAmount + (order.tax || 0) + shippingCost - (order.loyalty_points_used || 0);
              const date = new Date(order.created_at).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit'
              });

              return (
                <ModernAccordion 
                  key={order.order_id} 
                  sx={{ 
                    animation: `${fadeIn} 0.4s ease-out`,
                    background: theme.palette.mode === 'light' 
                      ? 'linear-gradient(145deg, #ffffff, #f8f8f8)'
                      : 'linear-gradient(145deg, #1e1e1e, #2a2a2a)'
                  }}
                >
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary' }} />}
                    sx={{
                      p: 3,
                      '& .MuiAccordionSummary-content': {
                        alignItems: 'center',
                        gap: 2
                      }
                    }}
                  >
                    <Badge
                      badgeContent={order.is_online_order ? "Online" : null}
                      color="secondary"
                      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                      sx={{
                        '& .MuiBadge-badge': {
                          top: 8,
                          left: 8,
                          borderRadius: 2,
                          px: 1,
                          fontSize: '0.6rem',
                          textTransform: 'uppercase'
                        }
                      }}
                    >
                      <Avatar 
                        sx={{ 
                          bgcolor: 'transparent',
                          width: 44,
                          height: 44,
                          fontSize: '0.875rem',
                          border: `2px solid ${theme.palette.primary.main}`,
                          color: theme.palette.primary.main,
                          fontWeight: 600,
                          boxShadow: theme.shadows[1]
                        }}
                      >
                        #{order.order_number.split('-').pop()}
                      </Avatar>
                    </Badge>
                    
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ 
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 0.5
                      }}>
                        <Typography variant="body1" fontWeight={600}>
                          {order.customer_name || "Walk-in Customer"}
                        </Typography>
                        <Chip
                          label={order.payment_status.toUpperCase()}
                          size="small"
                          variant="outlined"
                          sx={{ 
                            borderRadius: 20,
                            borderColor: order.payment_status === 'paid' 
                              ? theme.palette.success.main 
                              : theme.palette.warning.main,
                            color: order.payment_status === 'paid' 
                              ? theme.palette.success.main 
                              : theme.palette.warning.main,
                            bgcolor: order.payment_status === 'paid' 
                              ? `${theme.palette.success.light}15` 
                              : `${theme.palette.warning.light}15`
                          }}
                        />
                      </Box>
                      
                      <Box sx={{ 
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <Typography variant="caption" color="text.secondary">
                          {date}
                        </Typography>
                        <Typography variant="h6" fontWeight={700}>
                          ${totalPrice.toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails sx={{ pt: 0, px: 3, pb: 3 }}>
                    <Divider sx={{ 
                      mb: 3, 
                      borderColor: 'divider',
                      borderWidth: '1px',
                      opacity: 0.5
                    }} />
                    
                    {/* Shipping Information Section */}
                    {order.shipping_address && (
                      <Box sx={{ 
                        mb: 3, 
                        p: 2, 
                        backgroundColor: theme.palette.mode === 'light' 
                          ? theme.palette.grey[100] 
                          : theme.palette.grey[800],
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.divider}`
                      }}>
                        <Typography variant="subtitle1" fontWeight={600} sx={{ 
                          mb: 1, 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1,
                          color: theme.palette.text.primary
                        }}>
                          <LocalShippingIcon fontSize="small" /> SHIPPING DETAILS
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.primary">
                              <strong>Recipient:</strong> {order.shipping_address.full_name || order.customer_name}
                            </Typography>
                            <Typography variant="body2" color="text.primary">
                              <strong>Address:</strong> {order.shipping_address.address}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.primary">
                              <strong>City:</strong> {order.shipping_address.city}
                            </Typography>
                            <Typography variant="body2" color="text.primary">
                              <strong>State:</strong> {order.shipping_address.state}
                            </Typography>
                            <Typography variant="body2" color="text.primary">
                              <strong>Postal Code:</strong> {order.shipping_address.zip_code}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.primary">
                              <strong>Phone:</strong> {order.shipping_address.phone || order.customer_phone || 'Not provided'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.primary">
                              <strong>Email:</strong> {order.customer_email || 'Not provided'}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    )}

                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                        ORDER ITEMS
                      </Typography>
                      {order.items.map((item, index) => (
                        <Box 
                          key={index} 
                          sx={{ 
                            display: 'flex',
                            gap: 2,
                            mb: 2,
                            alignItems: 'center',
                            p: 1.5,
                            borderRadius: 2,
                            backgroundColor: theme.palette.action.hover
                          }}
                        >
                          <Avatar
                            src={item.image_url}
                            variant="rounded"
                            sx={{ 
                              width: 56, 
                              height: 56, 
                              borderRadius: 1.5,
                              boxShadow: theme.shadows[1]
                            }}
                          />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body1" fontWeight={500}>
                              {item.product_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {item.variant_name || formatAttributes(item.attributes)}
                            </Typography>
                          </Box>
                          <Box textAlign="right">
                            <Typography variant="body2" color="text.secondary">
                              ${item.price.toFixed(2)} × {item.quantity}
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              ${(item.quantity * item.price).toFixed(2)}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>

                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={6} display="flex" alignItems="center" gap={1}>
                        <LocalAtmIcon fontSize="small" color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Payment Method
                          </Typography>
                          <Typography>
                            {order.payment_method?.includes('card') ? 'Credit Card' : 
                            order.payment_method?.toUpperCase() || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} display="flex" alignItems="center" gap={1}>
                        <LoyaltyIcon fontSize="small" color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Loyalty Used
                          </Typography>
                          <Typography color="primary">
                            -${order.loyalty_points_used?.toFixed(2) || '0.00'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} display="flex" alignItems="center" gap={1}>
                        <DiscountIcon fontSize="small" color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Discount
                          </Typography>
                          <Typography color="error">
                            -${discountAmount.toFixed(2)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} display="flex" alignItems="center" gap={1}>
                        <ReceiptIcon fontSize="small" color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Tax
                          </Typography>
                          <Typography>${order.tax?.toFixed(2)}</Typography>
                        </Box>
                      </Grid>
                     <Grid item xs={6} display="flex" alignItems="center" gap={1}>
  <LocalShippingIcon fontSize="small" color="action" />
  <Box>
    <Typography variant="caption" color="text.secondary">
      {order.is_delivery ? "Delivery Fee" : "Shipping Cost"}
    </Typography>

    <Typography color={order.is_delivery ? "primary.main" : "text.primary"}>
      ${order.is_delivery
        ? (Number(order.delivery_fee) || 0).toFixed(2)
        : (Number(order.shipping_cost) || 0).toFixed(2)}
    </Typography>

    {order.is_delivery && (
      <Typography
        variant="caption"
        sx={{ color: "text.secondary", display: "block" }}
      >
        Status: {order.delivery_status || "NEW"}
      </Typography>
    )}
  </Box>
</Grid>


                    </Grid>

                    <Box sx={{ 
                      backgroundColor: theme.palette.mode === 'light' 
                        ? theme.palette.primary.light 
                        : theme.palette.primary.dark,
                      borderRadius: 2,
                      p: 2,
                      mb: 3,
                      boxShadow: theme.shadows[1]
                    }}>
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'center' 
                      }}>
                        <Typography variant="body1" fontWeight={700} color="primary.contrastText">
                          TOTAL AMOUNT
                        </Typography>
                        <Typography variant="h6" fontWeight={700} color="primary.contrastText">
                          ${totalPrice.toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ 
                      display: 'flex',
                      gap: 2,
                      justifyContent: 'flex-end',
                      pt: 2
                    }}>
                      <Button
                        variant="outlined"
                        startIcon={<EmailIcon />}
                        size="medium"
                        onClick={() => sendReceipt(order.order_id)}
                        sx={{
                          borderRadius: 30,
                          px: 3,
                          textTransform: 'none'
                        }}
                      >
                        Email Receipt
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<PrintIcon />}
                        size="medium"
                        onClick={() => generateOrderPDF(order)}
                        sx={{
                          borderRadius: 30,
                          px: 3,
                          textTransform: 'none',
                          boxShadow: theme.shadows[2],
                          '&:hover': {
                            boxShadow: theme.shadows[4]
                          }
                        }}
                      >
                        Print Receipt
                      </Button>
                    </Box>
                  </AccordionDetails>
                </ModernAccordion>
              );
            })}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OrdersPage;